import { useCallback, useEffect, useRef, useState } from "react"
import Loader from "../../../../components/Loader"
import { StepProps } from "../../../../components/Stepper"
import { useRequest } from "../../../../hooks/useRequest"
import { BuyData, CreatedOrder } from "../../../../types/steps/buy"
import { useNavigate } from "react-router-dom"
import CancelOrderDialog, { CancelOrderDialogHandle } from "../../../../components/CancelOrderDialog"
import PaymentHoldBanner from "../../../../components/PaymentHoldBanner"
import CardPaymentFrame from "../../../../features/buy/CardPaymentFrame"
import PaymentCheckingPopup from "../../../../features/buy/PaymentCheckingPopup"
import { userStore } from "../../../../store/userStore"
import { appStore } from "../../../../store/appStore"

export default function PayStatus({ params }: StepProps<BuyData>) {
    const route = useNavigate()
    const { request } = useRequest()
    const { user, setUser } = userStore()
    const { city } = appStore()
    const cancelRef = useRef<CancelOrderDialogHandle>(null)
    const [order, setOrder] = useState<CreatedOrder | null>(null)
    const [checkingPayment, setCheckingPayment] = useState(false)
    const paymentUrl = params.paymentRedirectUrl

    const refreshUser = useCallback(() => {
        if (!user) return
        request("GET", `user?city_id=${city.id}`)
            .then(data => setUser(data))
            .catch(() => {})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [city.id, setUser, user])

    const getOrder = useCallback(async () => {
        const data = await request<CreatedOrder>("GET", `user/order/${params.orderId}`)
        setOrder(data)
        if (data.status.id === 3) {
            refreshUser()
            route("/tickets")
        } else if (data.status.id === 5) {
            route(-1)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.orderId, refreshUser, route])

    useEffect(() => {
        void getOrder()
        const intervalId = window.setInterval(() => {
            void getOrder()
        }, checkingPayment || paymentUrl ? 1500 : 5000)

        return () => window.clearInterval(intervalId)
    }, [checkingPayment, getOrder, paymentUrl])

    const cancelAction = async () => {
        await request("DELETE", `user/order/${params.orderId}`)
    }

    const onExitClick = () => {
        cancelRef.current?.open({
            orderId: params.orderId,
            onCancel: cancelAction,
            onSuccess: () => route(-1),
        })
    }

    if (checkingPayment) {
        return (
            <>
                <PaymentCheckingPopup />
                <CancelOrderDialog ref={cancelRef} />
            </>
        )
    }

    if (paymentUrl) {
        return (
            <>
                <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="shrink-0 px-4 pt-3">
                        <PaymentHoldBanner
                            createdAt={order?.created_at ?? params.orderCreatedAt}
                            expiredAt={order?.expired_at ?? params.orderExpiredAt}
                        />
                    </div>
                    <CardPaymentFrame
                        inline
                        url={paymentUrl}
                        orderId={params.orderId}
                        onReturnDetected={() => {
                            setCheckingPayment(true)
                            void getOrder()
                        }}
                        onClose={onExitClick}
                        onFrameNavigated={() => {
                            void getOrder()
                        }}
                    />
                </div>
                <CancelOrderDialog ref={cancelRef} />
            </>
        )
    }

    return (
        <>
            <div className="flex flex-col items-center justify-center gap-4 p-4 pb-[max(1rem,var(--safe-bottom))]">
                <div className="flex w-full max-w-[383px] flex-col gap-5">
                    <PaymentHoldBanner
                        createdAt={order?.created_at ?? params.orderCreatedAt}
                        expiredAt={order?.expired_at ?? params.orderExpiredAt}
                    />
                    <Loader />
                    <div className="text-center text-[22px]/[14px] font-semibold tracking-[0.135px]">Проводим оплату...</div>
                    <div className="text-center text-[15px] font-semibold tracking-[-0.116px] text-[#9D9BAD]">
                        Это может занять некоторое время. Пожалуйста, не закрывайте приложение.
                    </div>
                </div>
                <button
                    onClick={onExitClick}
                    className="bg-text/15 h-[50px] w-[383px] cursor-pointer rounded-[9px] text-[16px]/[21px] font-semibold tracking-[-0.32px]">
                    Отменить покупку
                </button>
            </div>
            <CancelOrderDialog ref={cancelRef} />
        </>
    )
}
